import React, { useRef, useState } from 'react';
import {
  computeRenderLayers,
  layerBgValue,
  useBackgroundStore,
} from './BackgroundProvider';

export default function BGMiniPreview({
  bg,
  height = 220,
  interactive = true,
  label,
  maxOffset = 14,
  showLabel = true,
  // SceneBuilder passes its own resolver: a layer being uploaded right now is
  // only in its draft, so the store's map (built from saved backgrounds) has no
  // URL for it yet.
  resolveSrc: resolveSrcProp,
}) {
  const { resolveSrc: storeResolveSrc } = useBackgroundStore();
  const resolveSrc = resolveSrcProp || storeResolveSrc;
  const ref = useRef(null);
  const [mx, setMx] = useState(0);
  const [my, setMy] = useState(0);
  const s = bg?.settings || {};
  const intensity = (s.parallax ?? 100) / 100;
  const layers = bg ? computeRenderLayers(bg) : [];
  const isStaticOrVideo = bg?.kind === 'static' || bg?.kind === 'video';
  const isVideoBg = bg?.kind === 'video' && bg?.mediaType === 'video';

  const onMove = (e) => {
    if (!interactive || !ref.current || isStaticOrVideo) return;
    const r = ref.current.getBoundingClientRect();
    setMx(((e.clientX - r.left) / r.width - 0.5) * 2);
    setMy(((e.clientY - r.top) / r.height - 0.5) * 2);
  };
  const onLeave = () => {
    setMx(0);
    setMy(0);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{
        position: 'relative',
        height,
        borderRadius: 12,
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
        background: '#f3f4f6',
      }}
    >
      {bg &&
        layers.map((L) => {
          const mult = (L.depth / 100) * intensity * maxOffset;
          const resolved = resolveSrc(L.src);
          const transform = `translate3d(${(-mx * mult + (L.offsetX || 0)).toFixed(2)}px, ${(-my * mult + (L.offsetY || 0)).toFixed(2)}px, 0) scale(${L.scale / 100}) rotate(${L.rotate || 0}deg)`;
          const commonStyle = {
            position: 'absolute',
            inset: `-${Math.max(mult * 2, 20)}px`,
            transform,
            filter: L.blur ? `blur(${L.blur}px)` : 'none',
            opacity: (L.opacity ?? 100) / 100,
            mixBlendMode:
              L.blend && L.blend !== 'normal' ? L.blend : undefined,
            transition: interactive
              ? 'transform 60ms linear'
              : 'transform 500ms ease-out',
          };
          if (isVideoBg) {
            return (
              <video
                key={L.id}
                src={resolved || ''}
                autoPlay
                loop
                muted
                playsInline
                style={{
                  ...commonStyle,
                  objectFit: 'cover',
                  width: '100%',
                  height: '100%',
                }}
              />
            );
          }
          return (
            <div
              key={L.id}
              style={{
                ...commonStyle,
                background: layerBgValue(resolved),
              }}
            />
          );
        })}
      {bg && s.dim > 0 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `rgba(0,0,0,${s.dim / 100})`,
          }}
        />
      )}
      {bg && s.overlayAlpha > 0 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: s.overlayColor || '#fff',
            opacity: s.overlayAlpha / 100,
          }}
        />
      )}
      {showLabel && (
        <div
          style={{
            position: 'absolute',
            left: 16,
            bottom: 16,
            right: 16,
            padding: '10px 14px',
            borderRadius: 10,
            background: 'rgba(255,255,255,0.72)',
            backdropFilter: 'blur(14px) saturate(140%)',
            WebkitBackdropFilter: 'blur(14px) saturate(140%)',
            border: '1px solid rgba(255,255,255,0.6)',
            boxShadow: '0 4px 14px -8px rgba(10,10,10,0.3)',
          }}
        >
          <div style={{ font: '600 13px Inter', color: '#0a0a0a' }}>
            {label || bg?.name || 'No background'}
          </div>
          <div
            style={{ font: '400 11px Inter', color: '#4a5565', marginTop: 2 }}
          >
            {bg?.kind === 'scene'
              ? `${layers.length} layers · Move cursor to see parallax`
              : bg?.kind === 'video'
              ? bg?.mediaType === 'gif'
                ? 'Animated GIF'
                : 'Looping video'
              : 'Static image'}
          </div>
        </div>
      )}
    </div>
  );
}
